import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, ReplaySubject, of } from 'rxjs';
import { ApiService } from '@app/core/services/api.service';
import { JwtService } from '@app/core/services/jwt.service';
import { User } from '@app/shared';
import { map, distinctUntilChanged, delay } from 'rxjs/operators';
import { OAuthService } from '@app/core/services/oauth.service';
export interface RegisterContext {
  UserName: string;
  Password: string;
  Email: string;
  participantId: string;
  roleId: string;
  roleType: string;
}

export interface ResetPasswordContext {
  userId: number;
  password: string;
}

export interface ChangePasswordContext{
  password: string;
}

export interface LockContext{
  username: any;
}
export interface UpdatePreferenceContext {
  participantId: string;
  email: string;
  optIn: string;

}



export interface BulkEmailContext {
  body: string;
  subject: string;
  url: string;
  userId: string;
  bulkNotificationId: string;
}

@Injectable()
export class UserService {
  private currentUserSubject = new BehaviorSubject<User>({} as User);
  public currentUser = this.currentUserSubject.asObservable().pipe(distinctUntilChanged());
  private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();
  public authTokenStale: string = 'stale_auth_token';
  public authTokenNew: string = 'new_auth_token';
  public currentToken: string;
  constructor(
    private apiService: ApiService,
    private oauthService: OAuthService,
    private http: HttpClient,
    private jwtService: JwtService
  ) { }

  // Verify JWT in localstorage with server & load user's info.
  // This runs once on application startup.
  populate() {
    // If JWT detected, attempt to get & store user's info
    if (this.jwtService.getToken()) {
      this.apiService.get('/user')
        .subscribe(
          data => this.setAuth(data.user),
          err => this.purgeAuth()
        );
    } else {
      // Remove any potential remnants of previous auth states
      this.purgeAuth();
    }
  }
  setAuth(user: User) {
    // Save JWT sent from server in localstorage
    this.jwtService.saveToken(user.token);
    // Save Token with UserData sent from server in localstorage
    //this.jwtService.saveTokenWithUserData(user);
    // Save participantId sent from server in localstorage
    //this.jwtService.saveParticipantId(user.participantId);
    // Save UserID from server to localstorage
    //this.jwtService.saveCurrentUserID(user.id);
    // Set current user data into observable
    this.currentUserSubject.next(user);
    // Set isAuthenticated to true
    this.isAuthenticatedSubject.next(true);
  }
  purgeAuth() {
    // Remove JWT from localstorage
    this.jwtService.destroyToken();
    // Set current user to an empty object
    this.currentUserSubject.next({} as User);
    // Set auth status to false
    this.isAuthenticatedSubject.next(false);
  }
  authenticate(type, credentials): Observable<any> {
    let user: User = {} as any;
    let params = new URLSearchParams();
    params.append('username', credentials.username);
    params.append('password', credentials.password);
    params.append('grant_type', 'password');
    
    return this.apiService.postToAuthServer('/oauth/token', params.toString())
      .pipe(map(
        data => {
          user.id = data.user_id;
          user.username = credentials.username;
          user.token = data.access_token;
          user.refreshToken = data.refresh_token;
          user.participantId = data.participant_id;
          user.email = data.email_address;
          user.lastLoginDate = data.last_login_date;
          this.jwtService.saveUserRole(data.role);
          this.setAuth(user);
          return user;
        }
      ));
  }
  getTokenFromAuthServer(): Observable<any> {
    let params = new URLSearchParams();
    params.append('grant_type', 'client_credentials');
    return this.apiService.postToAuthServer('/oauth/token', params.toString())
      .pipe(map(
        data => {
          //this.jwtService.saveTokenWithUserData(data);
          this.jwtService.saveToken(data.access_token);
          return data.access_token;
        }
      ));
  }
  refreshToken(): Observable<string> {
    /*
        The call that goes in here will use the existing refresh token to call
        a method on the oAuth server (usually called refreshToken) to get a new
        authorization token for the API calls.
    */
    return of(this.authTokenNew).pipe(delay(200));
  }
  getCurrentUser(): User {
    return this.currentUserSubject.value;
  }
  // Update the user on the server (email, pass, etc)
  update(user): Observable<User> {
    return this.apiService
      .put('/user', { user })
      .pipe(map(data => {
        // Update the currentUser observable
        this.currentUserSubject.next(data.user);
        return data.user;
      }));
  }
  registerUser(context: RegisterContext): Observable<any> {
    const data = {
      "username": context.UserName,
      "password": context.Password,
      "emailAddress": context.Email,
      "participantId": context.participantId,
      "roleId" :  context.roleId,
      "roleType":  context.roleType
    };
    return this.apiService.post('/users', data)
      .pipe(
        map(
          res  =>  {
            //console.log("response" +  res);
            return res
          },
          err => {
            console.log("error response" + err);
          }
        )
      );
  }
  resetPassword(context: ResetPasswordContext): Observable<any> {
    const data ={'userId': context.userId, 'password': context.password};    
    return this.apiService.post('/reset/password',data)
      .pipe(
        map(
          res => {
            //console.log("response"+ res);          
            return res
          },
          err => {
            console.log("error response"+ err);
          }
        )
      );
  }

  changePassword(context: ChangePasswordContext): Observable<any> {
    const data ={'password': context.password};    
    return this.apiService.post('/change/password',data)
      .pipe(
        map(
          res => {
            console.log("response"+ res);          
            return res
          },
          err => {
            console.log("error response"+ err);
          }
        )
      );
  }

  lockUser(userName: string): Observable<any> {
    return this.apiService.getWithHeaders('/users/lock/' + userName).pipe(
      map(
        res  =>  {
          //console.log("response" +  res);
          return res
        },
        err => {
          console.log("error response" + err);
          return err;
        }
      )
    );
  }


  // lockUser(userName: string): Observable<any> {
  //   return this.apiService.get('/users/lock/' + userName).pipe(
  //     map(
  //       res  =>  {
  //         //console.log("response" +  res);
  //         return res
  //       },
  //       err => {
  //         console.log("error response" + err);
  //         return err;
  //       }
  //     )
  //   );
  // }

  // lockUser(context: LockContext): Observable<any> {
  //   const data = { 'userName': context.username};
  //   return this.apiService.post('/users/lock', data)
  //   .pipe(
  //     map(
  //       res  =>  {
  //         //console.log("response" +  res);
  //         return res
  //       },
  //       err => {
  //         console.log("error response" + err);
  //         return err;
  //       }
  //     )
  //   );
  // }
  
  updatePreference(context: UpdatePreferenceContext): Observable<any> {
    let loggedInUser = this.jwtService.getLoggedInUser();
    const data = {
      't2ParticipantId': context.participantId, 'emailAddress': context.email,
      'optInInd': context.optIn, 'registeredEmail': loggedInUser.email
    };
    return this.apiService.post('/updatePreference', data)
      .pipe(
        map(
          res  =>  {
            //console.log("response" +  res);
            return res
          },
          err => {
            console.log("error response" + err);
          }
        )
      );
  }
  bulkEmail(context: BulkEmailContext): Observable<any> {
    const data = { 'emailBody': context.body, 'emailSubject': context.subject, 'customUrl': context.url, 'userId': context.userId, 'bulkNotificationId': context.bulkNotificationId };
    return this.apiService.post('/bulk/notifications', data)
      .pipe(
        map(
          res  =>  {
            //console.log("response" +  res);
            return res
          },
          err => {
            console.log("error response" + err);
          }
        )
      );
  }
  publishBulkEmail(bulkNotificationId: string): Observable<any> {
    const data = { 'bulkNotificationId': bulkNotificationId };
    return this.apiService.post('/bulk/notifications/publishers', data)
      .pipe(
        map(
          res  =>  {
            //console.log("response" +  res);
            return res
          },
          err => {
            console.log("error response" + err);
          }
        )
      );
  }
  logout(user_id: string): Observable<any> {
    var data = { 'user_id': user_id };
    return this.apiService.post('/userlogout', data)
      .pipe(
        map(
          res  =>  {
            //console.log("response" +  res);
            return res;
          },
          err => {
            console.log("error response" + err);
          }
        )
      );
  }
}
